import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, FolderInput } from 'lucide-react';
import type { Asset, AssetFolder } from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { assetName } from './mediaDisplay';

const ROOT = '__root__';

/**
 * Move one or more assets into a folder (or back to the "All" root). Selecting the root
 * clears the folder (`folderId: null`); selecting a folder sets that id.
 *
 * `assets` drives everything: an empty array keeps the modal closed, one asset names it in the
 * prompt, several move together through the bulk endpoint (one request, not N). Callers pass the
 * bulk selection straight in, so a single-card Move and a 40-item Move are the same flow.
 */
export function MoveToFolderModal({
  assets,
  folders,
  onClose,
  onMoved,
  tenantKey,
}: {
  assets: Asset[];
  folders: AssetFolder[];
  onClose: () => void;
  /** Fired after a successful move — lets the page clear its selection. */
  onMoved?: () => void;
  tenantKey: string;
}) {
  const queryClient = useQueryClient();
  // Selection is the folder id, or ROOT for the unfiled "All" root.
  const [selected, setSelected] = useState<string>(ROOT);
  const [error, setError] = useState<string | null>(null);

  const open = assets.length > 0;
  const single = assets.length === 1 ? assets[0] : null;
  // Only meaningful for a single asset — a mixed bulk selection has no one "current" folder,
  // so a bulk move opens on the root rather than pretending one of them is the default.
  const currentFolderId = single?.folderId ?? null;

  useEffect(() => {
    if (!open) return;
    setSelected(currentFolderId ?? ROOT);
    setError(null);
  }, [open, currentFolderId]);

  const move = useMutation({
    mutationFn: () =>
      api.bulkMoveAssets(
        assets.map((a) => a.id),
        selected === ROOT ? null : selected,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['media', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['asset-folders', tenantKey] });
      onMoved?.();
      onClose();
    },
    onError: (err) =>
      setError(actionErrorText(err, `Could not move ${single ? 'this asset' : 'these assets'}.`)),
  });

  const options: { id: string; name: string }[] = [
    { id: ROOT, name: 'All (root)' },
    ...folders.map((f) => ({ id: f.id, name: f.name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move to folder"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={move.isPending} onClick={() => { setError(null); move.mutate(); }}>
            <FolderInput className="h-4 w-4" aria-hidden />
            Move here
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {open && (
          <p className="text-sm text-ink-muted">
            Choose a destination for{' '}
            <span className="font-medium text-ink">
              {single ? assetName(single) : `${assets.length} items`}
            </span>
            .
          </p>
        )}
        <div className="max-h-64 space-y-1 overflow-y-auto" role="radiogroup" aria-label="Folders">
          {options.map((opt) => {
            const active = selected === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelected(opt.id)}
                className={cn(
                  'focus-ring flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm',
                  active
                    ? 'border-ink-ghost bg-tint text-tint-fg'
                    : 'border-line text-ink-strong hover:bg-hover',
                )}
              >
                <span className="truncate">{opt.name}</span>
                {active && <Check className="h-4 w-4 shrink-0 text-tint-fg" aria-hidden />}
              </button>
            );
          })}
        </div>
        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
      </div>
    </Modal>
  );
}
